import { Box, Button, Skeleton, Stack, Typography, useMediaQuery } from "@mui/material";
import { ApiQueries, ContactAvatar, NoItems, NotificationList, useAuth, useSchool, type Notification, IndicatorChipsForStudent } from "daymap-react";
import { useNavigate, useParams } from "react-router-dom";
import { getNotificationsWithOnClick } from "../../home/components/notifications";
import { InfiniteData } from "@tanstack/react-query";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { PageContainer } from "../../../components/layout";


function Notifications() {
    const { studentId } = useParams();
    const md = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const { data, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = new ApiQueries.notifications.get({ studentId: Number(studentId), notificationsPerPage: 10 }).ToQuery();
    const { isPending: studentIsPending, data: student } = new ApiQueries.student.get({ studentId: Number(studentId) }).ToQuery();
    // convert paged data into flat array
    const infData = data as unknown as InfiniteData<Notification[]>;
    const notificationItems: Notification[] = [];
    infData?.pages.forEach((p) => p.map(n => {
        notificationItems.push(n);
    }));

    const {Get, IsImpersonating} = useAuth();
    const {baseUrl} = useSchool();


    const NotificationRead = async (id:number) => {
        if (IsImpersonating)
            return;
        
        await Get({
            url: baseUrl + `/lms/api/coms/notifications/setread/${id}`,
            data:null,
            config:null,
        });
    }
    if (isPending || studentIsPending)
        return <PageSkeleton />

    return <PageContainer>
        <Box display="flex" flexDirection="column" gap={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start', overflow: 'hidden', width: '100%' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box>
                        <ContactAvatar studentId={Number(studentId)} sx={{ width: md ? 50 : 80, height: md ? 50 : 80, }} variant="rounded" contactFullName={student?.DisplaynameSurname} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: "flex-end", gap: 1 }}>
                        <Typography variant="h1">{student!.DisplaynameSurname}</Typography>
                        <Box display="flex" gap={2}>
                            <LabelAndValue label="Code" value={student!.Code} />
                            <LabelAndValue label="Year" value={student!.Year} />
                            <LabelAndValue label="House" value={student!.House} />
                        </Box>
                    </Box>
                </Box>
            </Box>
            <IndicatorChipsForStudent studentId={Number(studentId)} />
            <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="h3">{student?.Displayname}'s Timeline</Typography>
                <NotificationList showStudent={false} notifications={getNotificationsWithOnClick(notificationItems!, navigate, NotificationRead)} getNext={() => { }} />
                {isFetchingNextPage && <>
                    <Skeleton variant="rectangular" height={60} />
                    <Skeleton variant="rectangular" height={60} />
                    <Skeleton variant="rectangular" height={60} />
                    <Skeleton variant="rectangular" height={60} />
                </>}
                {!isFetchingNextPage && hasNextPage && <Button startIcon={<ExpandMore />} onClick={() => fetchNextPage()}>Load more</Button>}
                {!isFetchingNextPage && !hasNextPage && <NoItems>End of timeline</NoItems>}
            </Box>
        </Box>
    </PageContainer>
}

type KeyAndValueProps = {
    label: string;
    value: string;
}
export const LabelAndValue: React.FC<KeyAndValueProps> = ({ label, value }) => {
    return <Box display="flex" flexDirection="column" gap={0}>
        <Typography variant="caption">{label}</Typography>
        <Typography variant="subtitle2">{value}</Typography>
    </Box>
}

function PageSkeleton() {
    return <PageContainer>
    <Stack spacing={1}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box>
                <Skeleton variant="rounded" width={40} height={40} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: "flex-end", gap: 1 }}>
                <Skeleton variant="text" width={70} sx={{ fontSize: '2rem'}} />
            </Box>
        </Box>
        <Skeleton variant="rectangular" height={60} />
        <Skeleton variant="rectangular" height={60} />
        <Skeleton variant="rectangular" height={60} />
        <Skeleton variant="rectangular" height={60} />
    </Stack>
</PageContainer>
}

export default Notifications;